import {
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import 'yup-phone';
import { Box } from '@mui/system';

import React from 'react';

import { useSaveOrganizationMutation } from '../../../common/apis/AdminApi';
import { ReactComponent as ChevronLeftIconBlue } from '../../../common/assets/icons/chevron_left_icon_blue.svg';
import { ReactComponent as LocationIcon } from '../../../common/assets/icons/location_blue_icon.svg';
import { useAppSelector } from '../../../common/hooks/reduxHooks';
import { OrganizationToSave } from '../../../common/types/Organization';
import { UserRoles } from '../../../common/types/User';
import SnackbarUtils from '../../../common/utils/SnackbarUtilsConfigurator';

import OrganizationInput from './components/OrganizationInput';

import { useFormik } from 'formik';

import OwnerAutocompleteInput from './components/OwnerAutocompleteInput';

import { useNavigate } from 'react-router-dom';
import { useUserRole } from 'src/common/hooks/useUserRole';
import { Pages } from 'src/common/constants/Pages';

const OrganizationEditSchema = Yup.object({
  name: Yup.string().required('Please enter an organization name').trim(),
  owner: Yup.string().required('Please select an owner'),
  address: Yup.string().required('Please enter an address').trim(),
  phone: Yup.string()
    .phone('US', false, 'Please enter a valid phone number')
    .required('Please enter a phone number')
    .trim(),
  // priceMobile: Yup.number().required(
  //   "Please enter a price for Mobile Licenses"
  // ),
  priceDesktop: Yup.number()
    .min(0, 'The price must be 0 or higher')
    .required('Please enter a price for Desktop Licenses'),
  priceNoteScans: Yup.number()
    .min(0, 'The price must be 0 or higher')
    .required('Please enter a price for Note Scans'),
});

const OrganizationCreateScreen: React.FC = () => {
  const [saveOrganization, { isLoading: saveIsLoading }] =
    useSaveOrganizationMutation();

  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.model);
  const { userRole } = useUserRole();

  const formik = useFormik<Yup.InferType<typeof OrganizationEditSchema>>({
    initialValues: {
      address: '',
      name: '',
      owner: '',
      phone: '',
      // priceMobile: 4.99,
      priceDesktop: 39.99,
      priceNoteScans: 0.99,
    },
    validationSchema: OrganizationEditSchema,
    onSubmit: async (values) => {
      const castedValues = OrganizationEditSchema.cast(values);
      saveOrganization(castedValues as OrganizationToSave)
        .unwrap()
        .then((res) => {
          SnackbarUtils.success(res.message);
          formik.resetForm();
          navigate(Pages.organizations);
        });
    },
  });

  if (userRole !== UserRoles.SUPER_ADMIN) {
    return <></>;
  }

  return (
    <Box
      style={{
        paddingLeft: '40px',
        paddingRight: '40px',
        paddingTop: '54px',
      }}
    >
      <Button
        disabled={saveIsLoading}
        sx={{
          marginBottom: '16px',
        }}
        onClick={() =>
          window.history.state && window.history.state.idx > 0
            ? navigate(-1)
            : navigate(Pages.organizations)
        }
        color={'darkBlue'}
        variant={'text'}
        startIcon={<ChevronLeftIconBlue />}
      >
        Back
      </Button>
      <Grid
        container
        spacing={8}
        direction={{ xs: 'column', md: 'row' }}
        alignItems='center'
        sx={{
          marginTop: '0px',
        }}
      >
        <Backdrop
          open={saveIsLoading || !user}
          sx={{
            zIndex: 99,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress color='secondary' size='128px' />
        </Backdrop>

        <Grid item xs={6} width='100%'>
          <Stack spacing={2}>
            <OrganizationInput
              formik={formik}
              formikFieldKey='name'
              title='Organization Name'
            />
            <OwnerAutocompleteInput
              value={formik.values.owner}
              formik={formik}
              onChange={(newValue) =>
                formik.setFieldValue('owner', newValue ?? '')
              }
            />
            <OrganizationInput
              formik={formik}
              formikFieldKey='address'
              title='Address'
              startIcon={<LocationIcon />}
            />
            <OrganizationInput
              formik={formik}
              formikFieldKey='phone'
              title='Phone Number'
            />
          </Stack>
        </Grid>
        <Grid item xs={6} width='100%'>
          <Box
            sx={{
              backgroundColor: 'rgba(34, 123, 239, 0.05)',
              p: '40px',
              borderRadius: '16px',
            }}
          >
            <Stack spacing={2}>
              <Typography
                variant='h6'
                fontSize={18}
                marginBottom='24'
                fontWeight={700}
              >
                Pricing
              </Typography>
              {/* <OrganizationInput
                formik={formik}
                formikFieldKey="priceMobile"
                title="Price mobile license"
                isCurrency
                disabled={userRole !== UserRoles.SUPER_ADMIN}
              /> */}
              <OrganizationInput
                formik={formik}
                formikFieldKey='priceDesktop'
                title='Price desktop license'
                isCurrency
                disabled={userRole !== UserRoles.SUPER_ADMIN}
              />
              <OrganizationInput
                formik={formik}
                formikFieldKey='priceNoteScans'
                title='Price note scans'
                isCurrency
                disabled={userRole !== UserRoles.SUPER_ADMIN}
              />
            </Stack>
          </Box>
        </Grid>
        <Button
          onClick={() => formik.handleSubmit()}
          disableElevation
          disabled={saveIsLoading}
          color='primary'
          variant='contained'
          sx={{
            borderRadius: '50px',
            height: '50px',
            width: '132px',
            mt: '48px',
            ml: '78px',
            mb: '40px',
          }}
        >
          <Typography color='white'>Save Changes</Typography>
        </Button>
      </Grid>
    </Box>
  );
};

export default OrganizationCreateScreen;
